// Modul 40 B4 — eine Bestellung als Karte.
//
// Die Karte ist der Arbeitsplatz des Einkaufs: zugeklappt eine Zeile mit dem Stand, aufgeklappt
// alles, was zu dieser Bestellung besorgt werden muss — und zwar AENDERBAR an Ort und Stelle.
// Die Akte bleibt daneben bestehen; sie ist der Ort fuer Positionen, Ereignisse und alles,
// was hier bewusst nicht steht.
//
// Zwei Dinge, die man beim Aendern wissen muss:
//   1. Der Rumpf laedt LAZY. `usePurchaseOrder(offen ? id : null)` — ohne den Schalter zoege
//      eine Liste mit 40 Bestellungen 80 Abfragen beim ersten Rendern.
//   2. Der Kopf klappt auf Klick um. Jeder Knopf IM Kopf (Akte, CSV, PDF) muss den Klick
//      deshalb anhalten, sonst loest er nebenbei das Auf- und Zuklappen mit aus.
import { useState } from "react";
import { ChevronDown, ChevronRight, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatUsd, orderStatusLabel } from "@/lib/purchase-format";
import { usePurchaseOrder, usePurchaseDocuments, bestellungPdfHerunterladen } from "@/api/purchase";
import type { PurchaseOrderListRow } from "@/api/purchase";
import { MengenZelle } from "./MengenZelle";
import { formatEuroGenau } from "./InlineZellen";
import { dokumenteJeZeile, kartenSummen, heuteBerlin } from "./zeilen";
import {
  InvoiceZeile, ZahlungenZeile, ContainerZeile, SonstigeZeile, NotizZeile,
} from "./KartenZeilen";

/**
 * Die Meldung zu einem gescheiterten PDF-Abruf.
 *
 * Der PDF-Weg laeuft ueber `downloadPdf` (api/warehouse.ts) und wirft KEINE `ApiError`:
 * er holt den Satz des Servers selbst aus dem Fehler-Rumpf und wirft ihn als
 * gewoehnlichen `Error`. Nur wenn kein Satz mitkam, baut er sich
 * „Download fehlgeschlagen (HTTP 502)" — und genau diese technische Zeile gehoert nicht
 * an die Karte. Dieselbe Regel wie `serverSatz` fuer die Zellen, nur fuer den anderen Weg.
 */
function pdfMeldung(err: unknown): string {
  const satz = err instanceof Error ? err.message : "";
  return satz && !satz.startsWith("Download fehlgeschlagen (HTTP")
    ? satz
    : "PDF konnte nicht geladen werden";
}

/** Ein Wert im Kartenkopf: kleines Wort, darunter die Zahl. */
function Kennzahl(p: { wort: string; wert: string; stark?: boolean }) {
  return (
    <span className="flex flex-col leading-tight">
      <span className="text-[10px] uppercase tracking-wide text-ink-2">{p.wort}</span>
      <span className={`tabular-nums ${p.stark ? "font-medium text-ink" : "text-ink-2"}`}>{p.wert}</span>
    </span>
  );
}

export function BestellKarte(p: {
  row: PurchaseOrderListRow;
  offen: boolean;
  onToggle: () => void;
  onOpen: (id: number) => void;
  onCsv: (id: number) => void;
  darfPreise: boolean;
}) {
  const akte = usePurchaseOrder(p.offen ? p.row.id : null);
  const dokumente = usePurchaseDocuments(p.offen ? p.row.id : null);
  const [pdfFehler, setPdfFehler] = useState<string | null>(null);
  const [pdfLaeuft, setPdfLaeuft] = useState(false);
  const summen = kartenSummen(p.row);
  const file = akte.data?.order;
  const zeilen = dokumenteJeZeile(dokumente.data ?? []);
  // Der Server liefert die Container bereits nach id sortiert; die Karte sortiert trotzdem
  // selbst, weil die laufende Nummer im Titel (#<nr>-1, -2) sonst zwischen zwei Ladevorgaengen
  // springen koennte — und eine Container-Nummer, die springt, ist schlimmer als keine.
  const container = [...(file?.shipments ?? [])].sort((a, b) => a.id - b.id);
  const heute = heuteBerlin();

  return (
    <article className="rounded-lg border border-line bg-surface">
      {/* Klick irgendwo auf den Kopf klappt um; der Knopf links macht dasselbe fuer Tastatur
          und Screenreader (und traegt aria-expanded). */}
      <div
        className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2"
        onClick={p.onToggle}
      >
        <button
          type="button"
          aria-expanded={p.offen}
          aria-label={`Bestellung ${p.row.order_number} ${p.offen ? "zuklappen" : "aufklappen"}`}
          onClick={(e) => { e.stopPropagation(); p.onToggle(); }}
          className="inline-flex items-center gap-1 font-medium"
        >
          {p.offen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          {p.row.order_number}
        </button>

        <span className="text-sm text-ink-2">{p.row.supplier_name ?? "—"}</span>
        <span className="rounded bg-background px-1.5 py-0.5 text-xs">{orderStatusLabel(p.row.status)}</span>
        {/* Bewusst ein <div>, kein <span>: die Bestandszusicherung A10 sucht das EINE
            SPAN-Element mit dem Text "3 / 1200". Ein umschliessendes <span> traegt denselben
            Text und macht die Suche mehrdeutig — der Bestandstest waere rot, ohne dass die
            Anzeige falsch waere. */}
        <div className="text-xs tabular-nums" title="Positionen / Gesamtmenge">
          <MengenZelle row={p.row} />
        </div>

        <span className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <Kennzahl wort="Rechnung" wert={formatUsd(p.row.invoice_total_usd)} stark />
          <Kennzahl wort="bezahlt" wert={`${formatUsd(p.row.paid_usd)} / ${formatEuroGenau(p.row.paid_eur)}`} />
          <Kennzahl wort="offen" wert={formatUsd(summen.offenUsd)} stark />
          <Kennzahl wort="Kosten" wert={formatEuroGenau(summen.kostenEur)} />
          <Kennzahl wort="nächste ETA" wert={p.row.next_eta ? p.row.next_eta.slice(0, 10) : "—"} />
        </span>

        {p.row.we_pending_count > 0 ? (
          <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
            WE offen ({p.row.we_pending_count})
          </span>
        ) : null}

        <span className="ml-auto flex items-center gap-1">
          <Button
            size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs"
            aria-label={`Akte ${p.row.order_number}`}
            onClick={(e) => { e.stopPropagation(); p.onOpen(p.row.id); }}
          >
            Akte
          </Button>
          <Button
            size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs"
            aria-label={`CSV für Bestellung ${p.row.order_number}`}
            onClick={(e) => { e.stopPropagation(); p.onCsv(p.row.id); }}
          >
            <Download className="size-3" /> CSV
          </Button>
          <Button
            size="sm" variant="outline" className="h-7 gap-1 px-2 text-xs"
            aria-label={`PDF für Bestellung ${p.row.order_number}`}
            disabled={pdfLaeuft}
            onClick={(e) => {
              e.stopPropagation();
              setPdfFehler(null);
              setPdfLaeuft(true);
              // `void` warf die abgelehnte Zusage weg: der Knopf sah aus, als haette er
              // gearbeitet, und es kam nur nie eine Datei (Befund Reviewer 1).
              bestellungPdfHerunterladen(p.row.id, p.row.order_number)
                .catch((err) => setPdfFehler(pdfMeldung(err)))
                .finally(() => setPdfLaeuft(false));
            }}
          >
            <FileText className="size-3" /> {pdfLaeuft ? "PDF …" : "PDF"}
          </Button>
        </span>
        {pdfFehler ? (
          <span role="alert" className="w-full text-xs text-destructive">{pdfFehler}</span>
        ) : null}
      </div>

      {p.offen ? (
        <div className="px-3 pb-2">
          {akte.isError ? (
            // Ohne diesen Zweig stand „Lade Akte…" fuer immer da: die Abfrage war
            // abgeschlossen, nur eben erfolglos (Befund Reviewer 1).
            <div role="alert" className="flex items-center gap-2 py-3 text-xs text-destructive">
              Akte konnte nicht geladen werden.
              <button
                type="button"
                className="underline decoration-dotted underline-offset-4"
                onClick={() => void akte.refetch()}
              >
                Erneut versuchen
              </button>
            </div>
          ) : !file ? (
            <div className="py-3 text-xs text-ink-2">Lade Akte…</div>
          ) : (
            <>
              {/* Scheitert NUR die Dokument-Abfrage, saehe die Karte aus wie eine
                  Bestellung ohne Dateien — und jemand laedt sie ein zweites Mal hoch. */}
              {dokumente.isError ? (
                <div role="alert" className="py-1.5 text-xs text-destructive">
                  Dokumente konnten nicht geladen werden — die Zeilen unten zeigen deshalb keine Dateien.
                </div>
              ) : null}
              <InvoiceZeile file={file} docs={zeilen.invoice} darfPreise={p.darfPreise} />
              <ZahlungenZeile file={file} darfPreise={p.darfPreise} />
              {container.length === 0 ? (
                <div className="border-t border-line/70 py-1.5 text-xs text-ink-2">
                  — kein Container angelegt (Akte → Container)
                </div>
              ) : (
                container.map((s, i) => (
                  <ContainerZeile
                    key={s.id} shipment={s} nr={i + 1}
                    orderId={file.id} orderNumber={file.order_number}
                    docs={dokumente.data ?? []} darfPreise={p.darfPreise} heute={heute}
                  />
                ))
              )}
              <SonstigeZeile docs={zeilen.sonstige} shipments={container} orderId={file.id} />
              <NotizZeile file={file} darfPreise={p.darfPreise} />
            </>
          )}
        </div>
      ) : null}
    </article>
  );
}
